import { sortBy } from "lodash";
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Footer from "components/layout/Footer";
import PageTitle from "components/layout/PageTitle";
import { NumericFormat } from "react-number-format";
import { fillParent, footerFlex, scrollingBox } from "utils/base-styles";

import analyticsMetadata from "features/Group/data/analytics-data-metadata.json";
import { InfoOutlined } from "@mui/icons-material";
import { Fragment } from "react";
import {
  ANALYTICS_DATAPOINTS,
  ANALYTICS_STAT_TYPES,
  ANALYTICS_TIMEFRAME,
  useGroupAnalytics,
} from "features/Group";
import { Helmet } from "react-helmet-async";

const DEFAULT_TIME_VALUE = 60 * 60; // 3600 (60 * 60) for seconds

function FormatValue({ type, value, decimalScale }) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return null;
  }
  switch (type) {
    case ANALYTICS_STAT_TYPES.TIME:
      const totalInSeconds = value;
      let output = "";
      const hours = Math.floor(totalInSeconds / DEFAULT_TIME_VALUE);
      const minutes = Math.floor(
        (totalInSeconds - hours * DEFAULT_TIME_VALUE) / 60
      );
      const seconds =
        totalInSeconds - hours * DEFAULT_TIME_VALUE - minutes * 60;

      if (hours > 0) {
        output += `${hours}hrs `;
      }
      if (minutes > 0) {
        output += `${minutes}min `;
      }
      if (hours <= 0 && minutes <= 0 && seconds > 0) {
        output += `${seconds}sec`;
      }
      if (output.trim() === "") {
        output = "0";
      }

      return (
        <Typography fontWeight="bold" variant="subtitle1">
          {output}
        </Typography>
      );
    case ANALYTICS_STAT_TYPES.PERCENTAGE:
      const valueIsBetweenZeroAndOne = value > 0 && value < 1;
      let percentageDecimalScale = 0;
      if (valueIsBetweenZeroAndOne) {
        if (decimalScale == null || decimalScale === 0) {
          percentageDecimalScale = 1;
        } else {
          percentageDecimalScale = decimalScale;
        }
      } else if (decimalScale != null) {
        percentageDecimalScale = decimalScale;
      }
      return (
        <Typography
          component={NumericFormat}
          fontWeight="bold"
          variant="subtitle1"
          value={value}
          defaultValue="-"
          displayType="text"
          decimalScale={percentageDecimalScale}
          suffix={"%"}
        />
      );
    default:
      return (
        <Typography
          component={NumericFormat}
          fontWeight="bold"
          variant="subtitle1"
          value={value}
          defaultValue="-"
          decimalScale={decimalScale != null ? decimalScale : 0}
          displayType="text"
          thousandSeparator=","
          thousandsGroupStyle="thousand"
        />
      );
  }
}

export default function GroupAnalyticsPage() {
  const {
    timeframe,
    setTimeframe,
    analyticsOverview,
    overviewDataError,
    loading,
  } = useGroupAnalytics();

  return (
    <Box
      sx={{
        "@media screen": {
          ...fillParent,
          ...scrollingBox,
          ...footerFlex,
        },
        "@media print": {
          m: 3,
        },
      }}
    >
      <Helmet>
        <title>Sooth - Group Management - Group Analytics</title>
      </Helmet>
      <Stack
        direction="column"
        pb="50px"
        sx={{
          mr: { xs: 0, xl: "25vw" },
          "& .MuiDivider-root": {
            borderBottomWidth: "medium",
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="cemter"
        >
          <PageTitle sx={{ flex: 1 }}>Group Analytics</PageTitle>
          <FormControl sx={{ width: "200px", mt: 3 }}>
            <InputLabel id="timeframe-select-label" htmlFor="timeframe-select">
              Timeframe
            </InputLabel>
            <Select
              labelId="timeframe-select-label"
              slotProps={{
                input: {
                  id: "timeframe-select",
                },
              }}
              value={timeframe}
              label="Timeframe"
              onChange={(event) => {
                setTimeframe(event.target.value);
              }}
            >
              <MenuItem value={ANALYTICS_TIMEFRAME.LAST_SEVEN}>
                Last 7 Days
              </MenuItem>
              <MenuItem value={ANALYTICS_TIMEFRAME.LAST_THIRTY}>
                Last 30 Days
              </MenuItem>
              <MenuItem value={ANALYTICS_TIMEFRAME.ALL_TIME}>All Time</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        {overviewDataError != null && (
          <Alert severity="error">
            <AlertTitle>Error Getting Analytics Data</AlertTitle>
            {overviewDataError.message}
          </Alert>
        )}
        <Box
          sx={{
            display: { xs: "flex", sm: "grid" },
            flexDirection: "column",
            gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
            rowGap: 2,
            columnGap: 2,
            "@media print": {
              display: "grid",
            },
          }}
        >
          {loading
            ? Array.from(Array(6)).map((item, index) => (
                <Skeleton
                  variant="rounded"
                  sx={{ aspectRatio: 16 / 9, height: "unset" }}
                  key={`temporary-${index}`}
                />
              ))
            : sortBy(analyticsOverview, (dataPoint) => dataPoint.order).map(
                (dataPoint) => {
                  return (
                    analyticsMetadata[dataPoint.id] != null && (
                      <Card
                        key={dataPoint.id}
                        variant="outlined"
                        sx={({ shape, palette }) => ({
                          backgroundColor: palette.secondary.main,
                          borderRadius: shape.borderRadius / 4,
                          border: "none",
                          p: 1,
                        })}
                      >
                        <CardHeader
                          title={
                            <Stack direction="row" alignItems="center" gap={1}>
                              <Typography color="primary" variant="h4">
                                {analyticsMetadata[dataPoint.id].title}
                              </Typography>
                              {analyticsMetadata[dataPoint.id]?.info !=
                                null && (
                                <Tooltip
                                  arrow
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        p: 3,
                                        "& p + p": {
                                          mt: 2,
                                        },
                                      },
                                    },
                                  }}
                                  title={
                                    <Fragment>
                                      {analyticsMetadata[dataPoint.id].info
                                        .split("\n")
                                        .map((line, index) => (
                                          <Typography
                                            key={`${dataPoint.id}-info-line-${index}`}
                                          >
                                            {line}
                                          </Typography>
                                        ))}
                                    </Fragment>
                                  }
                                >
                                  <Typography color="primary">
                                    <InfoOutlined fontSize="inherit" />
                                  </Typography>
                                </Tooltip>
                              )}
                            </Stack>
                          }
                          disableTypography
                          titleTypographyProps={{
                            color: "primary",
                            variant: "h4",
                          }}
                        />
                        <CardContent>
                          <Stack justifyContent="center">
                            {sortBy(dataPoint.stats, (stat) => stat.order).map(
                              (stat) => (
                                <Box
                                  key={`${dataPoint.id}-stat-block-${stat.id}`}
                                >
                                  <Stack
                                    direction="row"
                                    gap={0.5}
                                    justifyContent="center"
                                    alignItems="baseline"
                                  >
                                    {analyticsMetadata[dataPoint.id][stat.id]
                                      ?.label != null && (
                                      <Typography variant="subtitle1">
                                        {
                                          analyticsMetadata[dataPoint.id][
                                            stat.id
                                          ].label
                                        }
                                        :
                                      </Typography>
                                    )}
                                    <FormatValue
                                      type={stat.type}
                                      value={stat.value}
                                      decimalScale={
                                        dataPoint.id ===
                                        ANALYTICS_DATAPOINTS.ACTIVE_USERS
                                          ? null
                                          : 1
                                      }
                                    />
                                  </Stack>
                                  {analyticsMetadata[dataPoint.id][stat.id]
                                    .qualifier != null && (
                                    <Typography
                                      variant="body2"
                                      align="center"
                                      my={0}
                                    >
                                      {
                                        analyticsMetadata[dataPoint.id][stat.id]
                                          .qualifier
                                      }
                                    </Typography>
                                  )}
                                </Box>
                              )
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    )
                  );
                }
              )}
        </Box>
        <Typography variant="h6" fontWeight="normal">
          More Analytics Coming Soon!
        </Typography>
        <Alert severity="info">
          <AlertTitle>Notes:</AlertTitle>
          <Box component="ul">
            <Typography component="li">
              Data may be delayed. Your statistics are updated at midnight (U.S.
              EST) each day.
            </Typography>
            <Typography component="li">
              <Typography component="span" fontStyle="italic">
                All Time
              </Typography>{" "}
              refers to the beginning of your Group Plan subscription or Jan 1,
              2025, whichever is more recent.
            </Typography>
          </Box>
        </Alert>
      </Stack>
      <Footer sx={{ px: 0 }} />
    </Box>
  );
}
